<script lang="ts" setup>
import { useLayouts } from '@layouts'
import { config } from '@layouts/config'
import type { NavLink } from '@layouts/types'
import { getComputedNavLinkToProp, isNavLinkActive } from '@layouts/utils'

import { hasPermission, hasResource } from '../plugins/casl'

defineProps<{
  item: NavLink
}>()

const { width: windowWidth } = useWindowSize()
const { isVerticalNavMini, dynamicI18nProps } = useLayouts()

const hideTitleAndBadge = isVerticalNavMini(windowWidth)
</script>

<template>
  <li v-if="hasPermission(item.permissions) && hasResource(item.resource)" :class="{ disabled: item.disable }" class="nav-link">
    <Component
      :is="item.to ? 'RouterLink' : 'a'"
      :class="{ 'router-link-active router-link-exact-active': isNavLinkActive(item, $router) }"
      v-bind="getComputedNavLinkToProp(item)"
    >
      <Component
        :is="config.app.iconRenderer || 'div'"
        v-if="item.icon"
        class="nav-item-icon"
        v-bind="item.icon as Record<string, any>"
      />
      <TransitionGroup name="transition-slide-x">
        <!-- 👉 Title -->
        <Component
          :is="config.app.enableI18n ? 'i18n-t' : 'span'"
          v-show="!hideTitleAndBadge"
          key="title"
          class="nav-item-title"
          v-bind="dynamicI18nProps(item.title, 'span')"
        >
          {{ item.title }}
        </Component>

        <!-- 👉 Badge -->
        <Component
          :is="config.app.enableI18n ? 'i18n-t' : 'span'"
          v-if="item.badgeContent"
          v-show="!hideTitleAndBadge"
          key="badge"
          :class="item.badgeClass"
          class="nav-item-badge"
          v-bind="dynamicI18nProps(item.badgeContent, 'span')"
        >
          {{ item.badgeContent }}
        </Component>

        <!-- 👉 Disabled lock indicator — only visible on hover, hinting why this item can't be clicked -->
        <Component
          :is="config.app.iconRenderer || 'div'"
          v-if="item.disable"
          v-show="!hideTitleAndBadge"
          key="lock"
          class="nav-item-lock"
          icon="mdi-lock"
        />
      </TransitionGroup>
    </Component>
  </li>
</template>

<style lang="scss">
.layout-vertical-nav {
  .nav-link a {
    display: flex;
    align-items: center;
    text-decoration: none;
  }

  .nav-item-lock {
    font-size: 18px;
    margin-inline-start: auto;
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
  }

  // `.disabled` sets `pointer-events: none` on this whole <li> (see the shared
  // .nav-link/.nav-group rule below) to block navigation — which would also
  // block :hover from ever firing. Restore hover-detection on the row itself,
  // while keeping the inner link inert so it still can't be clicked/navigated.
  .nav-link.disabled {
    pointer-events: auto !important;

    > a {
      pointer-events: none;
    }

    &:hover .nav-item-lock {
      opacity: 1;
    }
  }
}
</style>
